"use client"

import {
  Button,
  Card,
  CardContent,
  buildSmartAnalysisFindings,
  formatRelativeTimeIso,
  cn,
} from '@yeshuman/sdk'
import type { SmartAnalysisFinding } from '@yeshuman/sdk'
import { Bell, CheckCircle2, Sparkles, UserPlus } from 'lucide-react'

import type { ApplicantCard } from '../../hooks/use-opportunity-applicants'

export type SmartAnalysisFindingsProps = {
  applicants: ApplicantCard[]
  previousRunAt?: string | null
  onReviewPipeline: () => void
  onRunAnalysis?: () => void
  onNudge?: (applicationId: string) => void
  nudgePending?: boolean
  readOnly?: boolean
  className?: string
}

function findingIcon(kind: SmartAnalysisFinding['kind']) {
  switch (kind) {
    case 'new_applicants':
      return UserPlus
    case 'strong_invited':
      return Bell
    default:
      return Sparkles
  }
}

function FindingRow({
  finding,
  onReviewPipeline,
  onRunAnalysis,
  onNudge,
  nudgePending,
  readOnly = false,
}: {
  finding: SmartAnalysisFinding
  onReviewPipeline: () => void
  onRunAnalysis?: () => void
  onNudge?: (applicationId: string) => void
  nudgePending?: boolean
  readOnly?: boolean
}) {
  const Icon = findingIcon(finding.kind)

  if (finding.kind === 'new_applicants') {
    return (
      <div className="flex flex-col gap-3 border-b border-border/60 px-4 py-3 last:border-b-0 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
          <p className="text-sm text-foreground">
            {finding.count} new applicant{finding.count === 1 ? '' : 's'} since the last update.
          </p>
        </div>
        {readOnly ? null : (
          <Button type="button" variant="outline" size="sm" onClick={onReviewPipeline}>
            Review
          </Button>
        )}
      </div>
    )
  }

  if (finding.kind === 'strong_invited') {
    const nudgedLabel = finding.applicant.nudged_at
      ? `Nudged ${formatRelativeTimeIso(finding.applicant.nudged_at)}`
      : null

    return (
      <div className="flex flex-col gap-3 border-b border-border/60 px-4 py-3 last:border-b-0 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <Icon className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden />
          <p className="text-sm text-foreground">
            {finding.applicant.profile.name} is one of your strongest matches ({finding.percent}%)
            but has not applied — invited {finding.daysInvited}d ago.
          </p>
        </div>
        {readOnly ? null : nudgedLabel ? (
          <span className="text-sm text-muted-foreground">{nudgedLabel}</span>
        ) : onNudge ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={nudgePending}
            onClick={() => onNudge(finding.applicant.id)}
          >
            <Bell className="mr-1.5 shrink-0" aria-hidden />
            Nudge
          </Button>
        ) : null}
      </div>
    )
  }

  if (finding.kind === 'unanalysed') {
    return (
      <div className="flex flex-col gap-3 border-b border-border/60 px-4 py-3 last:border-b-0 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
          <p className="text-sm text-foreground">
            {finding.count} candidate{finding.count === 1 ? '' : 's'} do not have match scores yet.
          </p>
        </div>
        {readOnly ? null : onRunAnalysis ? (
          <Button type="button" variant="outline" size="sm" onClick={onRunAnalysis}>
            Run analysis
          </Button>
        ) : (
          <Button type="button" variant="outline" size="sm" onClick={onReviewPipeline}>
            Review pipeline
          </Button>
        )}
      </div>
    )
  }

  // 'clear' is only ever the sole finding and is rendered by the parent as a status line.
  return null
}

export function SmartAnalysisFindings({
  applicants,
  previousRunAt,
  onReviewPipeline,
  onRunAnalysis,
  onNudge,
  nudgePending,
  readOnly = false,
  className,
}: SmartAnalysisFindingsProps) {
  const findings = buildSmartAnalysisFindings(applicants, previousRunAt)

  // The all-clear state is a status line, not a report section — no card chrome.
  if (findings.length === 1 && findings[0].kind === 'clear') {
    return (
      <section aria-label="Talent findings" className={cn(className)}>
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <CheckCircle2 className="size-4 shrink-0 text-success" aria-hidden />
          Nothing new since the last update.
        </p>
      </section>
    )
  }

  return (
    <section aria-label="Talent findings" className={cn(className)}>
      <Card className="gap-0 overflow-hidden p-0">
        <CardContent className="p-0">
          {findings.map((finding, index) => (
            <FindingRow
              key={`${finding.kind}-${index}`}
              finding={finding}
              onReviewPipeline={onReviewPipeline}
              onRunAnalysis={onRunAnalysis}
              onNudge={onNudge}
              nudgePending={nudgePending}
              readOnly={readOnly}
            />
          ))}
        </CardContent>
      </Card>
    </section>
  )
}
